import classNames from "classnames";
import React from "react";
import { Link } from "react-router-dom";
import "./Button.scss";
import { Svg } from "../../Svg/Svg";
import { Typography } from "../../Typography/Typography";
import LoadingGif from "../../../../assets/images/gif/sdap-loading.json";
import Lottie from "lottie-react";

/**
 * Props for the Button component.
 *
 * @description The Button component is a versatile clickable button that supports multiple visual styles,
 * icons, and states. It follows BEM-style CSS class naming conventions.
 *
 * @property {string} title - The text content displayed on the button. Required.
 * @property {"primary" | "secondary" | "content"} [type="primary"] - The visual style type of the button.
 *   - "primary": Main action button with prominent styling
 *   - "secondary": Secondary action button with subdued styling
 *   - "content": Content-style button with minimal styling
 * @property {"solid" | "outlined"} [variant="solid"] - The visual variant of the button.
 *   - "solid": Filled background button
 *   - "outlined": Button with border only, transparent background
 * @property {string} [tooltip] - Tooltip text displayed on hover via the title attribute.
 * @property {boolean} [disabled=false] - Whether the button is disabled and non-interactive.
 * @property {string} [icon] - Name of the icon to display on the button. Uses the Svg component.
 * @property {number} [iconSize=16] - Size of the icon in pixels.
 * @property {string} [extraIcon] - Additional icon name for displaying a second icon.
 * @property {"left" | "right"} [iconPosition="right"] - Position of the main icon relative to the title.
 * @property {string} [className] - Additional CSS class names to apply to the button.
 * @property {() => void} [onClick] - Click event handler. Called when the button is clicked.
 * @property {string} [id] - HTML id attribute for the button element.
 * @property {boolean} [isLoading=false] - Whether the button is in a loading state (visual only).
 * @property {string} [testId] - Data-testid attribute for testing purposes.
 * @property {number} [tabIndex] - Tab index for keyboard navigation.
 *
 * @example
 * // Primary solid button with click handler
 * <Button
 *   title="Submit"
 *   type="primary"
 *   variant="solid"
 *   onClick={handleSubmit}
 * />
 *
 * @example
 * // Secondary outlined button with left icon
 * <Button
 *   title="Cancel"
 *   type="secondary"
 *   variant="outlined"
 *   icon="close"
 *   iconPosition="left"
 *   onClick={handleCancel}
 * />
 *
 * @example
 * // Button with extra icon
 * <Button
 *   title="Download"
 *   type="primary"
 *   icon="download"
 *   extraIcon="arrow-right"
 * />
 */
interface Props {
  title: string;
  type?: "primary" | "secondary" | "content";
  variant?: "solid" | "outlined";
  tooltip?: string;
  disabled?: boolean;
  icon?: string;
  iconSize?: number;
  extraIcon?: string;
  iconPosition?: "left" | "right";
  className?: string;
  onClick?: () => void;
  id?: string;
  isLoading?: boolean;
  testId?: string;
  tabIndex?: number;
}

/**
 * A versatile button component with support for multiple visual styles, icons, and states.
 *
 * @description The Button component renders a clickable HTML button element with configurable
 * styling, icons, and behavior. It uses BEM-style CSS classes for styling and supports
 * primary/secondary/content types with solid or outlined variants.
 *
 * @param {Props} props - The component props
 * @returns {JSX.Element} A button element with the configured styles and content
 *
 * @see {@link Props} for all available properties
 */
export const Button: React.FC<Props> = (props) => {
  const {
    tooltip,
    title,
    type = "primary",
    disabled,
    onClick,
    variant = "solid",
    icon,
    iconSize = 16,
    iconPosition = "right",
    className = "",
    id,
    extraIcon,
    testId,
    tabIndex,
  } = props;
  const wrapClass = classNames("button", {
    [`button--${type}`]: !!type,
    [`button--${variant}`]: !!variant,
    [`button--${type}--icon`]: !!icon,
    "disabled-button": disabled,
    [className]: !!className,
  });

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <>
      {icon && iconPosition === "left" ? (
        <button
          title={tooltip}
          className={wrapClass}
          disabled={disabled}
          onClick={handleClick}
          id={id}
          data-testid={testId}
          tabIndex={tabIndex}
        >
          <span className="button-typography">
            {icon && (
              <Svg
                icon={icon}
                width={iconSize}
                height={iconSize}
                className="left-icon"
                title={title}
              ></Svg>
            )}
            {title}
            {extraIcon && (
              <Svg
                icon={extraIcon}
                width={iconSize}
                height={iconSize}
                className="extra-icon-right"
                title={title}
              ></Svg>
            )}
          </span>
        </button>
      ) : (
        <button
          title={tooltip}
          className={wrapClass}
          disabled={disabled}
          onClick={handleClick}
          id={id}
          data-testid={testId}
          tabIndex={tabIndex}
        >
          <span className="button-typography">
            {extraIcon && (
              <Svg
                icon={extraIcon}
                width={iconSize}
                height={iconSize}
                className="extra-icon-left"
                title={title}
              ></Svg>
            )}
            {title}
            {icon && (
              <Svg
                icon={icon}
                width={iconSize}
                height={iconSize}
                className="right-icon"
                title={title}
              ></Svg>
            )}
          </span>
        </button>
      )}
    </>
  );
};

/**
 * Enum representing the target attribute values for link navigation.
 *
 * @description Specifies where to open the linked document when using LinkButton.
 * Maps directly to HTML anchor target attribute values.
 *
 * @property {string} BLANK - Opens the linked document in a new window or tab ("_blank")
 * @property {string} SELF - Opens the linked document in the same frame as it was clicked ("_self") - Default
 * @property {string} PARENT - Opens the linked document in the parent frame ("_parent")
 * @property {string} TOP - Opens the linked document in the full body of the window ("_top")
 */
export enum LinkTarget {
  BLANK = "_blank",
  SELF = "_self",
  PARENT = "_parent",
  TOP = "_top",
}

/**
 * Props for the LinkButton component.
 *
 * @description The LinkButton component renders a React Router Link styled as a button-like text link.
 * It supports optional icons, underlining, and various size options.
 *
 * @property {string | {}} to - The navigation target. Can be a URL string or a location object. Required.
 * @property {string} title - The text content displayed on the link. Required.
 * @property {boolean} [underlined=true] - Whether the link text should be underlined.
 * @property {LinkTarget} [target=LinkTarget.SELF] - Target attribute for the link navigation.
 * @property {string} [icon] - Name of the icon to display on the link. Uses the Svg component.
 * @property {number} [iconSize=16] - Size of the icon in pixels.
 * @property {"left" | "right"} [iconPosition="right"] - Position of the icon relative to the title.
 * @property {"sm" | "lg"} [size="sm"] - Size variant of the link text.
 * @property {string} [className] - Additional CSS class names to apply to the link container.
 * @property {string} [textClassName] - CSS class names to apply to the text span.
 * @property {(e?: any) => void} [onClick] - Click event handler. Receives the event object.
 * @property {string} [testId] - Data-testid attribute for testing purposes.
 * @property {string} [toolTip] - Tooltip text displayed on hover via the title attribute.
 * @property {string} [id] - HTML id attribute for the link element.
 * @property {string} [svgIconToolTip] - Tooltip for the icon element.
 * @property {number} [tabIndex] - Tab index for keyboard navigation.
 *
 * @example
 * // Basic link button
 * <LinkButton to="/home" title="Go Home" />
 *
 * @example
 * // Link with icon opening in new tab
 * <LinkButton
 *   to="/external"
 *   title="External Link"
 *   icon="external-link"
 *   target={LinkTarget.BLANK}
 * />
 *
 * @example
 * // Large link with click handler
 * <LinkButton
 *   to="/profile"
 *   title="View Profile"
 *   size="lg"
 *   icon="user"
 *   iconPosition="left"
 *   onClick={handleClick}
 * />
 */
interface LinkProps {
  to: string | {};
  title: string;
  underlined?: boolean;
  target?: LinkTarget;
  icon?: string;
  iconSize?: number;
  iconPosition?: "left" | "right";
  size?: "sm" | "lg";
  className?: string;
  textClassName?: string;
  onClick?: (e?: any) => void;
  testId?: string;
  toolTip?: string;
  id?: string;
  svgIconToolTip?: string;
  tabIndex?: number;
}

/**
 * A styled link component that navigates using React Router.
 *
 * @description LinkButton renders a React Router Link component styled as a text link with
 * optional icon support. It uses Typography component for text rendering and supports
 * different sizes and underline styles.
 *
 * @param {LinkProps} props - The component props
 * @returns {JSX.Element} A Link element with the configured styles and content
 *
 * @see {@link LinkProps} for all available properties
 * @see {@link LinkTarget} for target options
 */
export const LinkButton: React.FC<LinkProps> = (props) => {
  const {
    to,
    title,
    underlined = true,
    target = LinkTarget.SELF,
    icon,
    iconSize = 16,
    iconPosition = "right",
    className = "",
    size = "sm",
    textClassName = "",
    onClick,
    testId,
    toolTip,
    svgIconToolTip,
    id,
    tabIndex,
  } = props;

  const wrapClass = classNames("link link--text", {
    [`link--text--underlined`]: !!underlined,
    [`link--text--${size}`]: !!size,
    [`link--text--${size}--icon`]: !!icon,
    [className]: !!className,
  });
  return (
    <Link
      tabIndex={tabIndex}
      to={to}
      className={wrapClass}
      target={target}
      onClick={(e) => {
        if (onClick) onClick(e);
      }}
      data-testid={testId}
      id={id}
      title={toolTip}
    >
      {icon && iconPosition !== "left" ? (
        <Typography variant="body2" component="span">
          <span className={`${textClassName} button-typography`}>
            {title}
            {icon && (
              <Svg
                icon={icon}
                width={iconSize}
                height={iconSize}
                className="right-icon"
              ></Svg>
            )}
          </span>
        </Typography>
      ) : (
        <Typography variant="body2" component="span" className={textClassName}>
          <span className="button-typography">
            {icon && (
              <Svg
                icon={icon}
                width={iconSize}
                height={iconSize}
                className="left-icon"
                title={svgIconToolTip}
              ></Svg>
            )}
            {title}
          </span>
        </Typography>
      )}
    </Link>
  );
};

/**
 * Props for the LoadingButton component.
 *
 * @description The LoadingButton component is a button that displays a loading animation
 * during asynchronous operations. It uses Lottie animation for the loading spinner.
 *
 * @property {string} title - The text content displayed on the button. Required.
 * @property {"primary" | "secondary" | "content"} [type="primary"] - The visual style type of the button.
 *   - "primary": Main action button with prominent styling
 *   - "secondary": Secondary action button with subdued styling
 *   - "content": Content-style button with minimal styling
 * @property {"solid" | "outlined"} [variant="solid"] - The visual variant of the button.
 *   - "solid": Filled background button
 *   - "outlined": Button with border only, transparent background
 * @property {string} [tooltip] - Tooltip text displayed on hover via the title attribute.
 * @property {boolean} [disabled=false] - Whether the button is disabled and non-interactive.
 * @property {"left" | "right"} [loadingPosition="right"] - Position of the loading spinner relative to the title.
 * @property {string} [className] - Additional CSS class names to apply to the button.
 * @property {() => void} [onClick] - Click event handler. Called when the button is clicked.
 * @property {string} [id] - HTML id attribute for the button element.
 * @property {boolean} [isLoading=false] - Whether to show the loading spinner animation.
 * @property {string} [testId] - Data-testid attribute for testing purposes.
 *
 * @example
 * // Loading button with loading state
 * <LoadingButton
 *   title="Submit"
 *   isLoading={isSubmitting}
 *   onClick={handleSubmit}
 * />
 *
 * @example
 * // Loading button with left spinner
 * <LoadingButton
 *   title="Processing..."
 *   type="secondary"
 *   variant="outlined"
 *   loadingPosition="left"
 *   isLoading={isLoading}
 *   onClick={handleProcess}
 * />
 */
interface LoadingButtonProps {
  title: string;
  type?: "primary" | "secondary" | "content";
  variant?: "solid" | "outlined";
  tooltip?: string;
  disabled?: boolean;
  loadingPosition?: "left" | "right";
  className?: string;
  onClick?: () => void;
  id?: string;
  isLoading?: boolean;
  testId?: string;
}

/**
 * A button component with integrated loading animation.
 *
 * @description LoadingButton renders a button that displays a Lottie animation spinner
 * during loading states. The spinner position can be configured to appear on either
 * side of the button text. Unlike the regular Button component, this shows an actual
 * animated loading indicator when isLoading is true.
 *
 * @param {LoadingButtonProps} props - The component props
 * @returns {JSX.Element} A button element with optional loading animation
 *
 * @see {@link LoadingButtonProps} for all available properties
 */
export const LoadingButton: React.FC<LoadingButtonProps> = ({
  tooltip,
  title,
  type = "primary",
  disabled,
  onClick,
  variant = "solid",
  loadingPosition = "right",
  className = "",
  id,
  isLoading,
  testId,
}) => {
  const wrapClass = classNames("button", {
    [`button--${type}`]: !!type,
    [`button--${variant}`]: !!variant,
    "disabled-button": disabled,
    [className]: !!className,
  });

  const handleClick = () => {
    if (onClick) onClick();
  };

  return (
    <button
      title={tooltip}
      className={wrapClass}
      disabled={disabled}
      onClick={handleClick}
      id={id}
      data-testid={testId}
    >
      <span>
        <div className={"button-text-icon-container"}>
          {loadingPosition === "left" && isLoading && (
            <Lottie
              className="loading-button__spinner_left"
              animationData={LoadingGif}
              loop={true}
              style={{ width: 20, height: 20 }}
            />
          )}
          <span className="button-typography">{title}</span>
          {loadingPosition === "right" && isLoading && (
            <Lottie
              className="loading-button__spinner_right"
              animationData={LoadingGif}
              loop={true}
              style={{ width: 20, height: 20 }}
            />
          )}
        </div>
      </span>
    </button>
  );
};
